import React from "react";
import { observer } from "mobx-react";
import { Row, Col } from "react-bootstrap";
import { Line } from 'react-chartjs-2';
import { Report2Item } from "../../../../stores";
import { ru } from 'date-fns/locale';
import { formatShortDate, formatMoney, formatDateToMask } from "../../../../common/Formatting";

interface IProps {
    data: Report2Item[];
    dateFrom: Date;
    dateTo: Date;
    splitBy: "day" | "month"
}

@observer
export class BaseChart extends React.Component<IProps, {}> {
    constructor(props: IProps) {
        super(props);
        this.state = {
            splitBy: "month"
        };
    }

    render() {

        const labels: any[] = [];
        const localize: any = ru.localize;
        switch (this.props.splitBy) {
            case "month":
                for (let d = new Date(this.props.dateFrom); d <= this.props.dateTo; d.setMonth(d.getMonth() + 1)) {
                    labels.push({
                        key: formatDateToMask(d, "MM.yyyy"),
                        label: `${localize.month(d.getMonth(), { width: 'abbreviated' })}, ${d.getFullYear()}`,
                        saleSum: 0,
                        purchaseSum: 0,
                        orderCount: 0,
                        positionsCount: 0,
                        margin: 0
                    })
                }
                break;
            case "day":
                for (let d = new Date(this.props.dateFrom); d <= this.props.dateTo; d.setDate(d.getDate() + 1)) {
                    labels.push({
                        key: formatDateToMask(d, "dd.MM.yyyy"),
                        label: `${formatShortDate(d)}, ${localize.day(d.getDay(), { width: 'abbreviated' })}`,
                        saleSum: 0,
                        purchaseSum: 0,
                        orderCount: 0,
                        positionsCount: 0,
                        margin: 0
                    })
                }
                break;
        }



        this.props.data.forEach(item => {
            const year = new Date(item.date).getFullYear();
            const month = new Date(item.date).getMonth();
            let key = "";
            let label = "";
            switch (this.props.splitBy) {
                case "month":
                    key = formatDateToMask(item.date, "MM.yyyy");
                    label = `${localize.month(month, { width: 'abbreviated' })}, ${year}`;
                    break;
                case "day":

                    key = formatDateToMask(item.date, "dd.MM.yyyy");
                    label = `${formatShortDate(item.date)}, ${localize.day(item.date.getDay, { width: 'abbreviated' })}`;
                    break;
            }
            const index = labels.findIndex(w => w.key == key);
            if (index == -1) {
                labels.push({
                    key: key,
                    label: label,
                    saleSum: 0,
                    purchaseSum: 0,
                    orderCount: 0,
                    positionsCount: 0,
                    margin: 0
                })
            }
            else {
                labels[index].purchaseSum += item.purchaseSum || 0;
                labels[index].saleSum += item.saleSum || 0;
                labels[index].orderCount += item.ordersCount || 0;
                labels[index].positionsCount += item.positionsCount || 0;
                labels[index].margin += (item.saleSum || 0) - (item.purchaseSum || 0);
            }

        });

        const data = {

            datasets: [
                {
                    label: 'Продажи за период, количество заказов',
                    fill: false,
                    backgroundColor: 'rgba(75,192,192,0.4)',
                    borderColor: '#5a9ada',
                    pointBorderColor: 'rgba(75,192,192,1)',
                    pointBackgroundColor: '#fff',
                    pointHoverBackgroundColor: 'rgba(75,192,192,1)',
                    pointHoverBorderColor: 'rgba(220,220,220,1)',
                    data: labels.map(w => w.orderCount),
                    yAxisID: 'y-axis-2'
                },
                {
                    label: 'Продажи за период, количество позиций',
                    fill: false,
                    backgroundColor: 'rgba(75,192,192,0.4)',
                    borderColor: '#7ceaad',
                    pointBorderColor: 'rgba(75,192,192,1)',
                    pointBackgroundColor: '#fff',
                    pointHoverBackgroundColor: 'rgba(75,192,192,1)',
                    pointHoverBorderColor: 'rgba(220,220,220,1)',
                    data: labels.map(w => w.positionsCount),
                    yAxisID: 'y-axis-3'
                },
                {
                    label: 'Продажи за период, сумма продаж',
                    fill: false,
                    backgroundColor: 'rgba(75,192,192,0.4)',
                    borderColor: 'rgba(75,192,192,1)',
                    pointBorderColor: 'rgba(75,192,192,1)',
                    pointBackgroundColor: '#fff',
                    pointHoverBackgroundColor: 'rgba(75,192,192,1)',
                    pointHoverBorderColor: 'rgba(220,220,220,1)',
                    data: labels.map(w => formatMoney(w.saleSum)),
                    yAxisID: 'y-axis-1',
                },
                {
                    label: 'Продажи за период, сумма закупки',
                    fill: false,
                    backgroundColor: 'rgba(75,192,192,0.4)',
                    borderColor: '#ca7cea',
                    pointBorderColor: 'rgba(75,192,192,1)',
                    pointBackgroundColor: '#fff',
                    pointHoverBackgroundColor: 'rgba(75,192,192,1)',
                    pointHoverBorderColor: 'rgba(220,220,220,1)',
                    data: labels.map(w => formatMoney(w.purchaseSum)),
                    yAxisID: 'y-axis-4'
                },
                {
                    label: 'Сумма маржи',
                    fill: false,
                    backgroundColor: 'rgba(75,192,192,0.4)',
                    borderColor: '#ffc107',
                    pointBorderColor: 'rgba(75,192,192,1)',
                    pointBackgroundColor: '#fff',
                    pointHoverBackgroundColor: 'rgba(75,192,192,1)',
                    pointHoverBorderColor: 'rgba(220,220,220,1)',
                    data: labels.map(w => formatMoney(w.margin)),
                    yAxisID: 'y-axis-5'
                }
            ]
        };

        const options = {
            responsive: true,
            tooltips: {
                mode: 'label'
            },
            elements: {
                line: {
                    fill: false
                }
            },
            scales: {
                xAxes: [
                    {
                        display: true,
                        gridLines: {
                            display: true
                        },
                        labels: [...labels.map(w => w.label)]
                    }
                ],
                yAxes: [
                    {
                        type: 'linear',
                        display: true,
                        position: 'left',
                        id: 'y-axis-1',
                        gridLines: {
                            display: true
                        },
                        labels: {
                            show: true
                        }
                    },
                    {
                        type: 'linear',
                        display: true,
                        position: 'right',
                        id: 'y-axis-2',
                        gridLines: {
                            display: false
                        },
                        labels: {
                            show: true
                        }
                    },
                    {
                        type: 'linear',
                        display: true,
                        position: 'right',
                        id: 'y-axis-3',
                        gridLines: {
                            display: false
                        },
                        labels: {
                            show: true
                        }
                    },
                    {
                        type: 'linear',
                        display: true,
                        position: 'right',
                        id: 'y-axis-4',
                        gridLines: {
                            display: false
                        },
                        labels: {
                            show: true
                        }
                    },
                    {
                        type: 'linear',
                        display: true,
                        position: 'right',
                        id: 'y-axis-5',
                        gridLines: {
                            display: false
                        },
                        labels: {
                            show: true
                        }
                    }
                ]
            }
        };

        return <Row>
            <Col sm={12}>
                <Line
                    data={data}
                    options={options} />
            </Col>
        </Row >
    }
}